import React, { useState } from "react";
import {
  Box,
  Typography,
  Button,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  CircularProgress,
  Chip,
} from "@mui/material";
import {
  CreditCard,
  CheckCircle,
  Pending,
  AccountBalance,
  Warning,
} from "@mui/icons-material";
import { employerApi } from "../services/apiClient";
import { toast } from "react-toastify";
import { useQueryClient } from "@tanstack/react-query";

interface PaymentMethodSectionProps {
  paymentDetails: {
    status: string | null;
    authorisationURL: string | null;
  } | null;
}

const PaymentMethodSection: React.FC<PaymentMethodSectionProps> = ({
  paymentDetails,
}) => {
  const queryClient = useQueryClient();
  const [loading, setLoading] = useState(false);
  const [confirmManualDialogOpen, setConfirmManualDialogOpen] = useState(false);

  const handleSetupDirectDebit = async () => {
    setLoading(true);
    try {
      const response = await employerApi.post<{
        success: boolean;
        data: { authorisationURL: string | null; status: string };
      }>("/payment/setup");

      if (response.success && response.data?.authorisationURL) {
        // Open GoCardless in new tab
        window.open(response.data.authorisationURL, "_blank");
        toast.success("Redirecting to GoCardless to complete setup...");
        // Invalidate query to refresh payment status
        queryClient.invalidateQueries({ queryKey: ["companyConfiguration"] });
      } else {
        toast.error("Failed to initiate payment setup");
      }
    } catch (error: any) {
      console.error("Error setting up payment:", error);
      toast.error(error.response?.data?.message || "Failed to setup payment");
    } finally {
      setLoading(false);
    }
  };

  const handleCompleteSetup = () => {
    if (paymentDetails?.authorisationURL) {
      window.open(paymentDetails.authorisationURL, "_blank");
      toast.info("Opening GoCardless to complete setup...");
    }
  };

  const handleSetManual = async () => {
    setLoading(true);
    try {
      const response = await employerApi.post<{ success: boolean; message: string }>(
        "/payment/manual"
      );

      if (response.success) {
        toast.success("Payment method set to manual");
        setConfirmManualDialogOpen(false);
        // Invalidate query to refresh payment status
        queryClient.invalidateQueries({ queryKey: ["companyConfiguration"] });
      } else {
        toast.error("Failed to set payment to manual");
      }
    } catch (error: any) {
      console.error("Error setting payment to manual:", error);
      toast.error(error.response?.data?.message || "Failed to set payment to manual");
    } finally {
      setLoading(false);
    }
  };

  // No paymentDetails object
  if (!paymentDetails || !paymentDetails.status) {
    return (
      <Box>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
          <CreditCard sx={{ color: "text.secondary" }} />
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            Payment Method
          </Typography>
        </Box>

        <Alert severity="info" sx={{ mb: 2 }}>
          Payment method not configured. Please set up a payment method to continue.
        </Alert>

        <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
          <Button
            variant="contained"
            startIcon={loading ? <CircularProgress size={16} /> : <AccountBalance />}
            onClick={handleSetupDirectDebit}
            disabled={loading}
          >
            Set up Direct Debit
          </Button>
          <Button
            variant="outlined"
            onClick={() => setConfirmManualDialogOpen(true)}
            disabled={loading}
          >
            Pay Manually
          </Button>
        </Box>

        <Dialog
          open={confirmManualDialogOpen}
          onClose={() => setConfirmManualDialogOpen(false)}
        >
          <DialogTitle>Switch to Manual Payment?</DialogTitle>
          <DialogContent>
            <DialogContentText>
              By switching to manual payment, you will need to pay invoices manually.
              You can set up Direct Debit at any time later. Are you sure you want to continue?
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setConfirmManualDialogOpen(false)}>Cancel</Button>
            <Button
              onClick={handleSetManual}
              variant="contained"
              disabled={loading}
            >
              {loading ? <CircularProgress size={16} /> : "Confirm"}
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    );
  }

  // Status: pending
  if (paymentDetails.status === "pending") {
    return (
      <Box>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
          <Pending sx={{ color: "warning.main" }} />
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            Payment Method
          </Typography>
          <Chip
            label="Setup In Progress"
            color="warning"
            size="small"
            sx={{ ml: "auto" }}
          />
        </Box>

        <Alert severity="warning" sx={{ mb: 2 }}>
          Direct Debit setup is in progress. Please complete the setup on GoCardless.
        </Alert>

        <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
          <Button
            variant="contained"
            startIcon={<AccountBalance />}
            onClick={handleCompleteSetup}
          >
            Complete Setup
          </Button>
          <Button
            variant="outlined"
            onClick={() => setConfirmManualDialogOpen(true)}
            disabled={loading}
          >
            Switch to Manual Payment
          </Button>
        </Box>

        <Dialog
          open={confirmManualDialogOpen}
          onClose={() => setConfirmManualDialogOpen(false)}
        >
          <DialogTitle>Switch to Manual Payment?</DialogTitle>
          <DialogContent>
            <DialogContentText>
              This will cancel your Direct Debit setup. You will need to pay invoices manually.
              Are you sure you want to switch to manual payment?
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setConfirmManualDialogOpen(false)}>Cancel</Button>
            <Button
              onClick={handleSetManual}
              variant="contained"
              disabled={loading}
            >
              {loading ? <CircularProgress size={16} /> : "Confirm"}
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    );
  }

  // Status: active
  if (paymentDetails.status === "fulfilled" || paymentDetails.status === "active" || paymentDetails.status === "mandate_submitted") {
    return (
      <Box>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
          <CheckCircle sx={{ color: "success.main" }} />
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            Payment Method
          </Typography>
          <Chip
            label="Active"
            color="success"
            size="small"
            sx={{ ml: "auto" }}
          />
        </Box>

        <Alert severity="success" sx={{ mb: 2 }}>
          <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
            Active Direct Debit Mandate
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Your Direct Debit mandate is active. Payments will be automatically collected.
          </Typography>
        </Alert>
      </Box>
    );
  }

  // Status: manual
  if (paymentDetails.status === "manual") {
    return (
      <Box>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
          <Warning sx={{ color: "text.secondary" }} />
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            Payment Method
          </Typography>
          <Chip
            label="Manual"
            size="small"
            sx={{ ml: "auto" }}
          />
        </Box>

        <Alert severity="info" sx={{ mb: 2 }}>
          <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
            Manual Payment Enabled
          </Typography>
          <Typography variant="body2" color="text.secondary">
            You are set up to pay invoices manually. You can set up Direct Debit at any time.
          </Typography>
        </Alert>

        <Button
          variant="contained"
          startIcon={loading ? <CircularProgress size={16} /> : <AccountBalance />}
          onClick={handleSetupDirectDebit}
          disabled={loading}
        >
          Set up Direct Debit
        </Button>
      </Box>
    );
  }

  // Unknown status
  return (
    <Box>
      <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
        <CreditCard sx={{ color: "text.secondary" }} />
        <Typography variant="h6" sx={{ fontWeight: 600 }}>
          Payment Method
        </Typography>
      </Box>

      <Alert severity="warning" sx={{ mb: 2 }}>
        Unknown payment status: {paymentDetails.status}
      </Alert>

      <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
        <Button
          variant="contained"
          startIcon={loading ? <CircularProgress size={16} /> : <AccountBalance />}
          onClick={handleSetupDirectDebit}
          disabled={loading}
        >
          Set up Direct Debit
        </Button>
        <Button
          variant="outlined"
          onClick={() => setConfirmManualDialogOpen(true)}
          disabled={loading}
        >
          Pay Manually
        </Button>
      </Box>

      <Dialog
        open={confirmManualDialogOpen}
        onClose={() => setConfirmManualDialogOpen(false)}
      >
        <DialogTitle>Switch to Manual Payment?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            By switching to manual payment, you will need to pay invoices manually.
            Are you sure you want to continue?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmManualDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={handleSetManual}
            variant="contained"
            disabled={loading}
          >
            {loading ? <CircularProgress size={16} /> : "Confirm"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default PaymentMethodSection;
